import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { motion, AnimatePresence } from "framer-motion";
import { DollarSign, Flame, Sparkles, TrendingUp, MessageSquare, Bot, Activity, ShieldAlert } from "lucide-react";
import { AnimatedNumber } from "../components/AnimatedNumber";
import AiTeamHero from "../components/AiTeamHero";
import { Link } from "react-router-dom";
import { useAuth } from "../lib/auth";

const LOGO = "/ashra-logo.png";

const COLORS = {
  revenue_green: "text-emerald-600 bg-emerald-100",
  opportunity_orange: "text-amber-600 bg-amber-100",
  ai_blue: "text-blue-600 bg-blue-100",
  churn_red: "text-rose-600 bg-rose-100",
};
const ICONS = { ai_recovery: DollarSign, hot_lead: Flame, ai_followup: MessageSquare, payment: DollarSign, upsell: TrendingUp, comment_dm: Bot, webinar: Activity, churn_risk: ShieldAlert };

function Stat({ label, value, prefix = "", delta, color = "text-ai" }) {
  return (
    <div className="surface p-5">
      <p className="text-[11px] uppercase tracking-wider text-meta font-semibold">{label}</p>
      <p className={`mt-2 font-display text-3xl font-extrabold ${color}`}><AnimatedNumber value={value} prefix={prefix} /></p>
      {delta && <p className="mt-1 text-xs text-meta font-medium">{delta}</p>}
    </div>
  );
}

export default function CommandCenter() {
  const { user } = useAuth();
  const [summary, setSummary] = useState(null);
  const [activity, setActivity] = useState([]);
  const [recovery, setRecovery] = useState(null);
  const [opps, setOpps] = useState(null);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const load = async () => {
    try {
      const [s, a, o, rec, op] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/activity?limit=8"),
        api.get("/onboarding/status"),
        api.get("/recovery/center").catch(() => ({ data: null })),
        api.get("/opportunities/center").catch(() => ({ data: null })),
      ]);
      setSummary(s.data);
      setActivity(a.data);
      setNeedsOnboarding(!o.data.completed);
      setRecovery(rec.data);
      setOpps(op.data);
    } catch {}
  };

  useEffect(() => { load(); }, []);

  if (!summary) return <div className="p-10 text-meta">Loading…</div>;

  const firstName = (user?.name || "Creator").split(" ")[0];

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="command-center">
      <div className="px-6 lg:px-10 py-8 max-w-[1600px] mx-auto">
        {needsOnboarding && (
          <div className="mb-4 rounded-2xl bg-gradient-to-r from-violet-100 to-pink-100 border border-violet-200 p-4 flex items-center justify-between" data-testid="onboarding-banner">
            <div>
              <p className="text-sm font-bold text-violet-900">Finish onboarding to personalize your AI team</p>
              <p className="text-xs text-violet-800 font-medium">6 quick steps · 2 minutes</p>
            </div>
            <Link to="/onboarding" className="rounded-full ashra-gradient-bg px-4 py-2 text-xs text-white font-semibold shadow-sm">Continue setup</Link>
          </div>
        )}

        <div className="mb-6">
          <p className="text-sm text-meta">Good morning, {firstName}! 👋</p>
          <h1 className="mt-1 font-display text-4xl font-extrabold tracking-tight text-ink">
            Your <span className="ashra-gradient-text">AI Revenue Team</span>
          </h1>
          <p className="mt-1 text-ink-soft">is operating 24/7 to grow your business.</p>
        </div>

        {/* AI Team at Work hero */}
        <div className="grid lg:grid-cols-3 gap-4 mb-6">
          <div className="lg:col-span-2">
            <AiTeamHero
              logoUrl={LOGO}
              agentStats={{
                leads_today: summary.leads_today || 0,
                upsell_opportunities: opps?.totals?.opportunities || 0,
                active_chats: summary.active_chats || 0,
                ai_accuracy: summary.ai_performance_score,
                revenue_recovered: summary.revenue_recovered,
              }}
              stats={[
                { label: "Revenue today", value: `$${Math.round(summary.revenue_today).toLocaleString()}` },
                { label: "AI-assisted", value: `$${Math.round(summary.ai_assisted_revenue).toLocaleString()}` },
                { label: "HOT leads", value: summary.hot_leads, delta: summary.hot_leads > 0 ? "Needs attention" : "All quiet" },
                { label: "Automations", value: summary.active_automations, delta: summary.active_automations > 0 ? "Active" : "None yet" },
              ]}
            />
          </div>

          <div className="space-y-4">
            <div className="surface p-6">
              <div className="flex items-center justify-between">
                <p className="text-xs uppercase tracking-wider text-meta font-semibold">Revenue Goal</p>
                <span className="text-[11px] text-meta-soft">This Month</span>
              </div>
              {(() => {
                const target = Math.round((summary.revenue_total || 0) + (summary.forecast_week || 0));
                const achieved = Math.round(summary.revenue_total || 0);
                const remaining = Math.max(0, target - achieved);
                const pct = target > 0 ? Math.min(100, Math.round((achieved / target) * 100)) : 0;
                return (
                  <>
                    <p className="mt-2 font-display text-3xl font-extrabold text-ink">${target.toLocaleString()}</p>
                    <p className="text-xs text-meta">Target Revenue</p>
                    <div className="mt-3 h-2 rounded-full bg-zinc-100 overflow-hidden">
                      <div className="h-full rounded-full ashra-gradient-bg transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="mt-3 flex justify-between text-xs">
                      <div><p className="text-success font-bold">${achieved.toLocaleString()}</p><p className="text-meta">Achieved</p></div>
                      <div className="text-right"><p className="font-bold text-ink">${remaining.toLocaleString()}</p><p className="text-meta">Remaining</p></div>
                    </div>
                    {target === 0 && <p className="mt-3 text-[11px] text-meta-soft">Set a revenue target in Forge → Offers to start tracking.</p>}
                  </>
                );
              })()}
            </div>

            <div className="surface p-6">
              <p className="text-xs uppercase tracking-wider text-meta font-semibold mb-3">AI Performance</p>
              {summary.ai_performance_score != null ? (
                <div className="flex items-center gap-4">
                  <div className="relative h-20 w-20">
                    <svg className="h-20 w-20 -rotate-90">
                      <circle cx="40" cy="40" r="34" stroke="#F4F4F5" strokeWidth="8" fill="none" />
                      <circle cx="40" cy="40" r="34" stroke="url(#grad)" strokeWidth="8" fill="none" strokeDasharray={213} strokeDashoffset={213 * (1 - summary.ai_performance_score / 100)} strokeLinecap="round" />
                      <defs><linearGradient id="grad"><stop offset="0%" stopColor="#7C3AED" /><stop offset="100%" stopColor="#059669" /></linearGradient></defs>
                    </svg>
                    <div className="absolute inset-0 grid place-items-center font-display font-extrabold text-ink">{Math.round(summary.ai_performance_score)}%</div>
                  </div>
                  <div>
                    <p className="text-sm font-bold text-ink">AI Performance Score</p>
                    <p className="text-xs text-success font-semibold">● {summary.ai_performance_score >= 85 ? "Excellent" : summary.ai_performance_score >= 70 ? "Healthy" : "Needs tuning"}</p>
                  </div>
                </div>
              ) : (
                <div className="text-sm text-meta">
                  <p>No AI interactions yet.</p>
                  <p className="text-xs mt-1 text-meta-soft">Score appears after your first AI-handled conversation.</p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* KPI strip */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <Stat label="Revenue total" value={Math.round(summary.revenue_total)} prefix="$" color="text-ink" />
          <Stat label="Workforce-generated" value={Math.round(summary.ai_assisted_revenue)} prefix="$" color="text-ai" />
          <Stat label="Recovered" value={Math.round(summary.revenue_recovered)} prefix="$" color="text-success" />
          <Stat label="HOT leads" value={summary.hot_leads} delta={summary.hot_leads > 0 ? "Action needed" : "Up to date"} color="text-warning" />
        </div>

        {/* Activity + HOT leads */}
        <div className="grid lg:grid-cols-3 gap-4">
          <div className="lg:col-span-1 surface p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-ink">AI Activity Feed</h3>
              <span className="inline-flex items-center gap-1 text-[11px] text-success font-bold"><span className="pulse-dot" /> Live</span>
            </div>
            <div className="space-y-2 max-h-96 overflow-y-auto scrollbar-thin">
              {activity.length === 0 ? (
                <div className="text-center text-meta py-12 text-sm">
                  <p>No AI activity yet.</p>
                  <p className="text-xs mt-1 text-meta-soft">Events appear here as your AI workforce engages leads, recovers checkouts, and closes sales.</p>
                </div>
              ) : (
                <AnimatePresence initial={false}>
                  {activity.map((a) => {
                    const Icon = ICONS[a.kind] || Bot;
                    const c = COLORS[a.color] || COLORS.ai_blue;
                    return (
                      <motion.div key={a.id} layout initial={{ opacity: 0, x: -6 }} animate={{ opacity: 1, x: 0 }}
                        className="flex items-center gap-3 rounded-xl border border-line bg-zinc-50 hover:bg-white hover:shadow-sm transition px-3 py-2">
                        <span className={`h-8 w-8 rounded-lg grid place-items-center shrink-0 ${c}`}><Icon className="h-4 w-4" /></span>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold truncate text-ink">{a.title}</p>
                          <p className="text-[11px] text-meta truncate">{a.detail}</p>
                        </div>
                      </motion.div>
                    );
                  })}
                </AnimatePresence>
              )}
            </div>
          </div>

          <div className="lg:col-span-2 surface p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-display text-lg font-bold text-ink">HOT Leads</h3>
              <Link to="/leads" className="text-xs text-ai font-bold hover:underline">View All →</Link>
            </div>
            <div className="space-y-2">
              {(summary.hot_list || []).length === 0 ? (
                <div className="text-center text-meta py-12 text-sm">
                  <p>No HOT leads yet.</p>
                  <p className="text-xs mt-1 text-meta-soft">When inbound DMs or comments arrive, the highest-scoring leads land here for fast action.</p>
                </div>
              ) : (
                (summary.hot_list || []).slice(0, 6).map((l) => (
                  <div key={l.id} className="flex items-center gap-3 rounded-xl border border-line hover:bg-zinc-50 transition px-3 py-2.5" data-testid={`hot-lead-${l.id}`}>
                    <div className="h-9 w-9 rounded-full ashra-gradient-bg grid place-items-center text-xs font-bold text-white shrink-0">{l.name?.[0]}</div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-semibold truncate text-ink">{l.name}</p>
                      <p className="text-xs text-meta truncate">{l.handle} · Source: {l.source}</p>
                    </div>
                    <span className={`text-sm font-bold ${l.purchase_probability >= 0.8 ? "text-warning" : "text-meta"}`}>
                      {Math.round(l.purchase_probability * 100)}
                    </span>
                  </div>
                ))
              )}
            </div>
          </div>
        </div>

        {/* Live recommendations — sourced from real Recovery + Opportunities data */}
        <div className="mt-6 surface p-6">
          <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
            <div>
              <h3 className="font-display text-lg font-bold text-ink">What to do next</h3>
              <p className="text-xs text-meta">Live recommendations based on your workspace right now</p>
            </div>
            <Link to="/convert?tab=strategist" className="text-xs text-ai font-bold hover:underline">Open Strategist →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="cc-recommendations">
            {buildRecommendations(summary, recovery, opps).map((r, i) => (
              <div key={i} className="rounded-2xl border border-line bg-zinc-50 hover:bg-white hover:shadow-sm transition p-4">
                <div className={`h-8 w-8 rounded-lg bg-gradient-to-br ${r.color} mb-3 shadow-sm`} />
                <p className="text-sm font-bold text-ink">{r.title}</p>
                <p className="text-xs text-meta mt-1">{r.desc}</p>
                <Link to={r.to} className="mt-3 inline-flex text-xs text-ai font-bold hover:underline">{r.cta} →</Link>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

function buildRecommendations(summary, recovery, opps) {
  const out = [];
  if ((summary?.hot_leads || 0) > 0) {
    out.push({ title: "Close the HOT pipeline", desc: `${summary.hot_leads} leads ready to convert`, cta: "Open Capture", to: "/capture?tab=leads", color: "from-amber-400 to-orange-500" });
  }
  const openAbandoned = recovery?.summary?.open_abandoned_checkouts || 0;
  if (openAbandoned > 0) {
    out.push({ title: "Recover open checkouts", desc: `${openAbandoned} carts pending recovery`, cta: "Run Recovery", to: "/recover", color: "from-emerald-400 to-teal-500" });
  }
  const oppCount = opps?.totals?.opportunities || opps?.opportunities?.length;
  if (oppCount) {
    out.push({ title: "Top opportunities", desc: `${oppCount} surfaced by Insight`, cta: "View Opportunities", to: "/capture?tab=opportunities", color: "from-violet-400 to-fuchsia-500" });
  }
  if ((summary?.revenue_recovered || 0) === 0 && (recovery?.summary?.in_progress || 0) === 0) {
    out.push({ title: "Tune your Workforce", desc: "Adjust Co-Pilot / Autopilot per worker", cta: "Open Workforce", to: "/convert?tab=workforce", color: "from-rose-400 to-pink-500" });
  }
  out.push({ title: "Generate strategy brief", desc: "Decision-grade plan from real data", cta: "Run Strategist", to: "/convert?tab=strategist", color: "from-blue-400 to-cyan-500" });
  return out.slice(0, 4);
}
